/**
 * The reactive editor state (CLAUDE.md 14.1, docs/SHEETS.md 3).
 *
 * Everything the editor does passes through here: edit a cell, recompute what
 * depends on it, and nothing else. The dirty set comes from the graph, so a
 * ten-cell edit in a two-hundred-cell sheet runs ten cells.
 *
 * Drafts autosave to localStorage (14.1: "Engineers lose work; do not let a dropped
 * connection cost them an hour"). S1 has no server persistence at all, so this is
 * the only thing standing between an afternoon's work and a closed tab.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { SheetEngine, createSheetWorker } from './engine/client'
import type { EngineStatus, WorkerLike } from './engine/client'
import { buildRequests } from './engine/requests'
import { LIMITS } from './engine/limits'
import { buildGraph, downstreamOf } from './model/graph'
import type { SheetGraph } from './model/graph'
import {
  canMove,
  insertCell,
  moveCellWithDependencies,
  moveCellUnchecked,
  newCellId,
  removeCell,
  replaceCell,
} from './model/document'
import type { MoveCheck } from './model/document'
import type { Cell, CellId, CellResult, CellStatus, ResultValue, SheetDoc } from './model/types'

const DRAFT_PREFIX = 'calcdesk.sheet.'
const DEBOUNCE_MS = 300
const UNDO_DEPTH = 100

export interface UseSheetOptions {
  /** Injected in tests so the hook can run without a real Worker. */
  readonly createWorker?: () => WorkerLike
  /** Off in tests, so a suite cannot leak state between cases. */
  readonly persistDrafts?: boolean
}

export interface SheetController {
  readonly doc: SheetDoc
  readonly graph: SheetGraph
  readonly results: ReadonlyMap<CellId, CellResult>
  readonly selected: CellId | null
  readonly engineStatus: EngineStatus
  readonly evaluating: boolean
  /** Cells recomputed by the most recent pass, for the toolbar. */
  readonly lastPassSize: number

  select(id: CellId | null): void
  update(cell: Cell): void
  insert(cell: Cell, at?: number): void
  remove(id: CellId): void
  /** Checks a drop without performing it, so the UI can refuse before the release. */
  checkMove(id: CellId, to: number): MoveCheck
  move(id: CellId, to: number): void
  moveWithDependencies(id: CellId, to: number): void
  rename(title: string): void

  undo(): void
  redo(): void
  readonly canUndo: boolean
  readonly canRedo: boolean
}

function loadDraft(id: string): SheetDoc | null {
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + id)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const doc = parsed as SheetDoc
    return Array.isArray(doc.cells) ? doc : null
  } catch {
    return null
  }
}

function saveDraft(doc: SheetDoc): void {
  try {
    const raw = JSON.stringify(doc)
    if (raw.length > LIMITS.maxDocumentBytes) return
    localStorage.setItem(DRAFT_PREFIX + doc.id, raw)
  } catch {
    // Quota, private mode, or storage disabled. Losing the draft is bad; throwing
    // in the middle of a keystroke is worse.
  }
}

/** Structural errors from the graph, before the engine has said anything. */
function seedResults(graph: SheetGraph): Map<CellId, CellResult> {
  const out = new Map<CellId, CellResult>()
  for (const id of graph.order) {
    const analysis = graph.analysis.get(id)
    const error = graph.errors.get(id)
    out.set(id, {
      cellId: id,
      status: error ? 'error' : 'unevaluated',
      defines: analysis?.defines ?? [],
      ...(error ? { error } : {}),
    })
  }
  return out
}

export function useSheet(initial: SheetDoc, options: UseSheetOptions = {}): SheetController {
  const persist = options.persistDrafts ?? true

  const [doc, setDoc] = useState<SheetDoc>(() => {
    if (!persist) return initial
    const draft = loadDraft(initial.id)
    return draft ?? initial
  })
  const [results, setResults] = useState<ReadonlyMap<CellId, CellResult>>(new Map())
  const [selected, setSelected] = useState<CellId | null>(null)
  // Booting from the first render. The effect below starts it immediately, and
  // setting this inside the effect body would cascade a second render for nothing.
  const [engineStatus, setEngineStatus] = useState<EngineStatus>('booting')
  const [evaluating, setEvaluating] = useState(false)
  const [lastPassSize, setLastPassSize] = useState(0)

  const [past, setPast] = useState<SheetDoc[]>([])
  const [future, setFuture] = useState<SheetDoc[]>([])
  const engine = useRef<SheetEngine | null>(null)
  const scope = useRef<Record<string, ResultValue>>({})
  const dirty = useRef<Set<CellId>>(new Set())
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [, forcePass] = useState(0)

  const graph = useMemo(() => buildGraph(doc.cells), [doc.cells])

  // Boot the engine once. A failure is a state, not an exception: the document is
  // still readable and editable, it simply shows no numbers.
  useEffect(() => {
    const instance = new SheetEngine(options.createWorker ?? createSheetWorker)
    engine.current = instance
    let cancelled = false

    instance
      .boot()
      .then(() => {
        if (cancelled) return
        setEngineStatus('ready')
        // First pass covers everything.
        dirty.current = new Set(doc.cells.map((cell) => cell.id))
        forcePass((n) => n + 1)
      })
      .catch(() => {
        if (!cancelled) setEngineStatus('failed')
      })

    return () => {
      cancelled = true
      instance.dispose()
      engine.current = null
    }
    // Boot once for the lifetime of the editor; `doc` is read only to seed the
    // first pass and must not re-boot the worker on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Structural results land immediately, without waiting for the worker: an
  // undefined symbol is knowable from the graph alone, and showing it at once is
  // the difference between a responsive editor and a laggy one.
  useEffect(() => {
    setResults((previous) => {
      const seeded = seedResults(graph)
      for (const [id, seed] of seeded) {
        const prior = previous.get(id)
        if (prior && !seed.error && prior.status === 'ok' && !dirty.current.has(id)) {
          seeded.set(id, prior)
        }
      }
      return seeded
    })
  }, [graph])

  useEffect(() => {
    if (persist) saveDraft(doc)
  }, [doc, persist])

  // The reactive pass, debounced. Typing produces many documents a second and the
  // engine only needs the last one.
  useEffect(() => {
    if (engineStatus !== 'ready' || dirty.current.size === 0) return

    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const instance = engine.current
      if (!instance) return

      const pending = dirty.current
      dirty.current = new Set()
      const requests = buildRequests(doc, graph, pending)
      if (requests.length === 0) return

      setEvaluating(true)
      void instance
        .evaluate(requests, scope.current)
        .then((pass) => {
          // A superseded pass is the answer to a question already changed.
          if (pass.superseded) return
          scope.current = pass.scope
          setLastPassSize(pass.outcomes.length)
          setResults((previous) => {
            const next = new Map(previous)
            for (const outcome of pass.outcomes) {
              const analysis = graph.analysis.get(outcome.cellId)
              const status: CellStatus = outcome.ok ? 'ok' : 'error'
              next.set(outcome.cellId, {
                cellId: outcome.cellId,
                status,
                defines: analysis?.defines ?? [],
                ...(outcome.value ? { value: outcome.value } : {}),
                ...(outcome.matrix ? { matrix: outcome.matrix } : {}),
                ...(outcome.series ? { series: outcome.series } : {}),
                ...(outcome.error ? { error: outcome.error } : {}),
              })
            }
            return next
          })
        })
        .finally(() => setEvaluating(false))
    }, DEBOUNCE_MS)

    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [doc, graph, engineStatus])

  const commit = useCallback(
    (next: SheetDoc, changed: Iterable<CellId>) => {
      setPast((history) => [...history, doc].slice(-UNDO_DEPTH))
      setFuture([])
      for (const id of downstreamOf(graph, changed)) dirty.current.add(id)
      setDoc(next)
    },
    [doc, graph],
  )

  const update = useCallback(
    (cell: Cell) => {
      // The edited cell plus its downstream. Computed against the *old* graph on
      // purpose: if an edit removes a reference, whatever used to depend on it
      // still has to be revisited.
      commit(replaceCell(doc, cell), [cell.id])
    },
    [commit, doc],
  )

  const insert = useCallback(
    (cell: Cell, at?: number) => {
      if (doc.cells.length >= LIMITS.maxCells) return
      const index = at ?? doc.cells.length
      commit(insertCell(doc, cell, index), [cell.id])
      setSelected(cell.id)
    },
    [commit, doc],
  )

  const remove = useCallback(
    (id: CellId) => {
      const affected = [id, ...(graph.dependents.get(id) ?? [])]
      commit(removeCell(doc, id), affected)
      setSelected((current) => (current === id ? null : current))
    },
    [commit, doc, graph],
  )

  const checkMove = useCallback((id: CellId, to: number) => canMove(doc, id, to), [doc])

  const move = useCallback(
    (id: CellId, to: number) => {
      if (!canMove(doc, id, to).ok) return
      commit(
        moveCellUnchecked(doc, id, to),
        doc.cells.map((cell) => cell.id),
      )
    },
    [commit, doc],
  )

  const moveWithDependencies = useCallback(
    (id: CellId, to: number) => {
      commit(
        moveCellWithDependencies(doc, id, to),
        doc.cells.map((cell) => cell.id),
      )
    },
    [commit, doc],
  )

  const rename = useCallback(
    (title: string) => {
      setPast((history) => [...history, doc].slice(-UNDO_DEPTH))
      setFuture([])
      setDoc({ ...doc, title })
    },
    [doc],
  )

  const undo = useCallback(() => {
    const previous = past[past.length - 1]
    if (!previous) return
    setPast((history) => history.slice(0, -1))
    setFuture((history) => [doc, ...history])
    dirty.current = new Set(previous.cells.map((cell) => cell.id))
    setDoc(previous)
  }, [doc, past])

  const redo = useCallback(() => {
    const next = future[0]
    if (!next) return
    setFuture((history) => history.slice(1))
    setPast((history) => [...history, doc])
    dirty.current = new Set(next.cells.map((cell) => cell.id))
    setDoc(next)
  }, [doc, future])

  return {
    doc,
    graph,
    results,
    selected,
    engineStatus,
    evaluating,
    lastPassSize,
    select: setSelected,
    update,
    insert,
    remove,
    checkMove,
    move,
    moveWithDependencies,
    rename,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
  }
}

/** A new, empty sheet. */
export function emptySheet(id?: string): SheetDoc {
  return {
    id: id ?? newCellId(),
    title: '',
    cells: [{ id: newCellId(), type: 'math', latex: '' }],
    engineVersion: '1.0.0',
  }
}

/**
 * The document a freshly created sheet starts from.
 *
 * The server assigns the real id; the one here is only a placeholder so the shape is
 * complete, and the client never treats `document.id` as authoritative.
 */
export function emptySheetDocument(): SheetDoc {
  return emptySheet('new')
}
